import { DI } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { Dirs, eBundle } from "@app/helpers/dirs";
import { AuthService } from "@app/services/auth.service";
import { eViewType, IRootScope } from "@models";
import { StateProvider } from "@uirouter/angularjs";
import * as angular from "angular";
import { events } from "@tools";

export class StateHelper {
    constructor(
        private $stateProvider: StateProvider,
        private bundle: eBundle
    ) {
        
    }
    public getPluralName(name: string) {
        let plural = name;
        if (!name.endsWith("s")) {
            if (name.endsWith("y")) {
                plural = name.slice(0, -1) + "ies";
            } else {
                plural = name + "s";
            }
        }
        return plural;
    }

    public addState(o: {
        name: string,
        view?: Token,
        url?: string,
        params?: {},
        data?: any,
        allowAnonymous?: boolean,
        reloadOnSearch?: boolean,
        abstract?: boolean,
        templateName?: string,
        templateUrl?: string,
        template?: string,
        parent?: string,
        authorization?: (authService: AuthService, $rootScope: IRootScope) => boolean,
    }) {
        let ngInject = o.view && DI.get(o.view);
        let resolve = ngInject && Object.assign({}, ngInject.resolveObject);
        let nameSegment = o.name.split(".").last();
        let kebab = nameSegment.toKebabCase();
        if (!o.allowAnonymous && resolve) {
            resolve["auth"] = [
                Token.AuthService.key, Token.$RootScope.key,
                (authService: AuthService, $rootScope: IRootScope) => {
                    return authService.authPromise.then(() => {
                        if (o.authorization && !o.authorization(authService, $rootScope)) {
                            $rootScope.$broadcast(events.permissionDenied);
                        }
                    });
                }
            ];
        }
        else if (o.allowAnonymous) {
            if (!o.data) o.data = {};
            o.data.allowAnonymous = true;
        }

        this.$stateProvider.state(o.name, {
            url: angular.isDefined(o.url) ? o.url : `/${kebab}`,
            params: o.params,
            template: () => { return o.template || Dirs.get(o.templateUrl) || Dirs.view(o.templateName || kebab, this.bundle) },
            // templateUrl: o.templateUrl || Dirs.view(o.templateName || kebab, this.bundle),
            controller: ngInject && ngInject.construct,
            resolve: resolve,
            data: o.data,
            reloadOnSearch: o.reloadOnSearch,
            parent: o.parent
        });
    }

    public addSearchState(o: {
        name: string,
        view: Token,
        url?: string,
        authorization?: (authService: AuthService, $rootScope: IRootScope) => boolean,
    }) {
        let nameSegment = o.name.split(".").last();
        this.addState({
            name: o.name,
            view: o.view,
            url: o.url? o.url: `/${nameSegment.toKebabCase()}?search&searchid`,
            params: {
                search: { dynamic: true },
                searchid: { dynamic: true }
            },
            authorization: o.authorization,
        });
    }

    public addCrudStates(o: {
        name: string,
        view: Token,
        searchView: Token,
        isSnapBased?: boolean,
        templateUrl?: string,
        template?: string,
        // Lists possible state params that can be applied to a new entity. For instance,
        // quote outputs may initialize with an output type state param
        newStateParams?:string[],
        authorization?: (authService: AuthService, $rootScope: IRootScope) => boolean,
    }) {

        let nameSegment = o.name.split(".").last();
        let nameKebab = nameSegment.toKebabCase();
        let pluralName = this.getPluralName(nameSegment);
        let baseUrl = `/${pluralName.toKebabCase()}`;
        let statePrefix = o.name.substring(0, o.name.lastIndexOf("."));
        let editStatePrefix = `${statePrefix}${o.isSnapBased ? ".base" : ""}`;

        this.addSearchState({ // the 'search' view
            name: `${statePrefix}.${pluralName}`,
            view: o.searchView,
            authorization: o.authorization,
        });
        let newStateConfig = { // the 'new' view
            name: `${editStatePrefix}.${nameSegment}New`,
            url: `${baseUrl}/new`,
            templateName: nameKebab,
            templateUrl: o.templateUrl,
            template: o.template,
            view: o.view,
            data: { viewType: eViewType.new },
            authorization: o.authorization,
        };
        if (o.newStateParams) newStateConfig.url += "/:" + o.newStateParams.join("/:")
        this.addState(newStateConfig);
        this.addState({ // the 'edit' view
            name: `${editStatePrefix}.${nameSegment}Edit`,
            url: `${baseUrl}/:id${o.isSnapBased ? "?sel" : ""}`,
            templateName: nameKebab,
            templateUrl: o.templateUrl,
            template: o.template,
            view: o.view,
            data: { viewType: eViewType.edit },
            reloadOnSearch: !o.isSnapBased,
            authorization: o.authorization,
        });
    }
}
